import { GP_VIEW_PERMISSION } from '@supply-mover-2-0/shared';
import {
	DEFAULT_ORDER_STATUS_COLORS,
	OrderStatus,
	OrderStatusColors,
	OrderStatusLabels,
} from '../../../utils/orderStatus';
import type { ListBuilderColumn } from '../../types';

/**
 * Shared column definitions for order lists
 */

/**
 * Status and Origin column with status badge, alert icon, and origin badge
 * Uses centralized colors from orderStatus.ts for consistency across the application.
 */
export const createStatusOriginColumn = (): ListBuilderColumn => ({
	id: 'status-origin',
	fields: [
		{
			id: 'status-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'status',
					objectTable: 'order',
				},
			},
			styleConfig: {
				size: 'xs',
				colorRule: {
					type: 'conditional',
					rules: [
						// Alert status error takes priority
						{
							field: { fieldName: 'alertStatus' },
							operator: 'equals',
							value: '3',
							color: { type: 'danger' },
						},
						// Status-based colors from centralized config
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.UNSUBMITTED,
							color: OrderStatusColors[OrderStatus.UNSUBMITTED],
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.PENDING,
							color: OrderStatusColors[OrderStatus.PENDING],
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.SENT_TO_ERP,
							color: OrderStatusColors[OrderStatus.SENT_TO_ERP],
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.APPROVED,
							color: OrderStatusColors[OrderStatus.APPROVED],
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.SHIPPED,
							color: OrderStatusColors[OrderStatus.SHIPPED],
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.HELD,
							color: OrderStatusColors[OrderStatus.HELD],
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.CANCELLED,
							color: OrderStatusColors[OrderStatus.CANCELLED],
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.APPROVAL_REQUIRED,
							color: OrderStatusColors[OrderStatus.APPROVAL_REQUIRED],
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.OVERRIDE_REJECTED,
							color: OrderStatusColors[OrderStatus.OVERRIDE_REJECTED],
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.CANCELLATION_REQUESTED,
							color: OrderStatusColors[OrderStatus.CANCELLATION_REQUESTED],
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.SUBMITTED,
							color: OrderStatusColors[OrderStatus.SUBMITTED],
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.PARTIALLY_SHIPPED,
							color: OrderStatusColors[OrderStatus.PARTIALLY_SHIPPED],
						},
					],
					defaultColor: DEFAULT_ORDER_STATUS_COLORS,
				},
				valueMapping: [
					{
						value: OrderStatus.UNSUBMITTED,
						displayText: OrderStatusLabels[OrderStatus.UNSUBMITTED],
					},
					{
						value: OrderStatus.PENDING,
						displayText: OrderStatusLabels[OrderStatus.PENDING],
					},
					{
						value: OrderStatus.SENT_TO_ERP,
						displayText: OrderStatusLabels[OrderStatus.SENT_TO_ERP],
					},
					{
						value: OrderStatus.APPROVED,
						displayText: OrderStatusLabels[OrderStatus.APPROVED],
					},
					{
						value: OrderStatus.SHIPPED,
						displayText: OrderStatusLabels[OrderStatus.SHIPPED],
					},
					{
						value: OrderStatus.HELD,
						displayText: OrderStatusLabels[OrderStatus.HELD],
					},
					{
						value: OrderStatus.CANCELLED,
						displayText: OrderStatusLabels[OrderStatus.CANCELLED],
					},
					{
						value: OrderStatus.APPROVAL_REQUIRED,
						displayText: OrderStatusLabels[OrderStatus.APPROVAL_REQUIRED],
					},
					{
						value: OrderStatus.OVERRIDE_REJECTED,
						displayText: OrderStatusLabels[OrderStatus.OVERRIDE_REJECTED],
					},
					{
						value: OrderStatus.CANCELLATION_REQUESTED,
						displayText: OrderStatusLabels[OrderStatus.CANCELLATION_REQUESTED],
					},
					{
						value: OrderStatus.SUBMITTED,
						displayText: OrderStatusLabels[OrderStatus.SUBMITTED],
					},
					{
						value: OrderStatus.PARTIALLY_SHIPPED,
						displayText: OrderStatusLabels[OrderStatus.PARTIALLY_SHIPPED],
					},
				],
			},
			displayStyle: 'badge',
		},
		{
			// preventLineWrap keeps the flag on the status badge's line, to its
			// right — as a plain wrapping sibling it dropped below the badge
			// whenever a long status squeezed the column (PD-13616).
			id: 'alert-status-icon',
			preventLineWrap: true,
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'alertStatus',
					objectTable: 'order',
				},
			},
			displayStyle: 'icon',
			styleConfig: {
				size: 'lg',
				iconName: 'exclamation-triangle',
				colorRule: { type: 'theme', themeColor: 'danger' },
				tooltip:
					'This order has an error. Click the order to view full error details.',
			},
			conditionalVisibility: {
				field: 'alertStatus',
				condition: 'equals',
				value: '3',
			},
		},
		{
			// PD-13616: amber counterpart of the error icon above — orders at
			// alertStatus WARNING ('2') previously showed no flag at all even
			// though the order detail page renders a warning banner for them.
			// At most one of the two icons is visible per row.
			id: 'warning-status-icon',
			preventLineWrap: true,
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'alertStatus',
					objectTable: 'order',
				},
			},
			displayStyle: 'icon',
			styleConfig: {
				size: 'lg',
				iconName: 'exclamation-triangle',
				colorRule: { type: 'theme', themeColor: 'warning' },
				tooltip:
					'This order has a warning. Click the order to view full warning details.',
			},
			conditionalVisibility: {
				field: 'alertStatus',
				condition: 'equals',
				value: '2',
			},
		},
		{
			id: 'line-break-1',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'origin-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'origin', objectTable: 'order' },
			},
			styleConfig: {
				size: 'xs',
				// Origin is a CATEGORY: one NEUTRAL chip for every value (owner
				// decision 2026-08-03, PD-15196 option B) — colourless, so it
				// still does not compete with the status beside it, and it now
				// matches the embedded order lists' treatment.
				colorRule: { type: 'default' },
				valueMapping: [
					{ value: '1', displayText: 'CRM', color: { type: 'neutral' } },
					{ value: '2', displayText: 'ERP', color: { type: 'neutral' } },
					{
						value: '3',
						displayText: 'E-Commerce',
						color: { type: 'neutral' },
					},
				],
			},
			displayStyle: 'badge',
		},
	],
	headers: [
		{ id: 'h1-status', text: 'Status' },
		{ id: 'h1-origin', text: 'Origin' },
	],
	minWidth: 120,
	// Cap the column so a nowrap Status Text line (Pharmsource's seeded
	// migration field) truncates instead of ballooning the column to the
	// table's 480px content cap. Badges alone never reach this, so other
	// tenants are unaffected (PD-13616).
	maxWidth: 280,
	sortable: true,
	alignment: 'left',
	sortFields: [{ direction: 'ASC', fieldName: 'status', objectTable: 'order' }],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

/**
 * Order and ERP numbers column
 */
export const createOrderErpNumbersColumn = (): ListBuilderColumn => ({
	id: 'order-erp-numbers',
	fields: [
		{
			id: 'external-id-field',
			link: {
				type: 'object',
				enabled: true,
				idField: '_id',
				objectTable: 'order',
			},
			dataConfig: {
				type: 'conditional',
				conditions: [
					{
						field: 'externalId2',
						operator: 'not_equals',
						value: null,
						dataConfig: {
							type: 'field',
							field: { fieldName: 'externalId2', objectTable: 'order' },
						},
					},
				],
				defaultDataConfig: {
					type: 'field',
					field: { fieldName: 'externalId', objectTable: 'order' },
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'link' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-ids',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'internal-id-field',
			link: {
				type: 'object',
				enabled: true,
				idField: '_id',
				objectTable: 'order',
			},
			dataConfig: {
				type: 'field',
				field: { fieldName: '_id', objectTable: 'order' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'link' },
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{ id: 'h2-external', text: 'External ID' },
		{ id: 'h2-internal', text: 'Internal ID' },
	],
	minWidth: 100,
	sortable: true,
	alignment: 'left',
	sortFields: [{ direction: 'ASC', fieldName: 'id', objectTable: 'order' }],
	widthConfig: { mode: 'flexible', preset: 'small' },
	verticalAlignment: 'top',
});

/**
 * Client info column with name and address
 */
export const createClientInfoColumn = (): ListBuilderColumn => ({
	id: 'client-info',
	fields: [
		{
			id: 'client-name-field',
			link: {
				type: 'object',
				enabled: true,
				idField: '_fk_client__id',
				objectTable: 'client',
			},
			dataConfig: {
				type: 'field',
				field: { fieldName: 'clientName', objectTable: 'order' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'statusInfo' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-3',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'client-address-field',
			dataConfig: {
				type: 'template',
				template: '{clientAddress1} {clientAddress2}',
				templateFields: [],
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-4',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'client-city-state-field',
			dataConfig: {
				type: 'template',
				template: '{clientCity}, {clientState} {clientZip}',
				templateFields: [],
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-delivery',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			// PD-15423 — cross-account delivery destination. Renders nothing on
			// normal orders (deliveryClientName only present when set).
			id: 'delivery-client-field',
			dataConfig: {
				type: 'conditional',
				conditions: [
					{
						field: 'deliveryClientName',
						operator: 'not_equals',
						value: null,
						dataConfig: {
							type: 'template',
							template: 'Deliver to: {deliveryClientName}',
							templateFields: [],
						},
					},
				],
				defaultDataConfig: { type: 'custom_text', customText: '' },
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'statusInfo' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h3', text: 'Client' }],
	minWidth: 180,
	sortable: true,
	alignment: 'left',
	sortFields: [
		{ direction: 'ASC', fieldName: 'clientName', objectTable: 'order' },
	],
	widthConfig: { mode: 'flexible', preset: 'large' },
	verticalAlignment: 'top',
});

/**
 * Dates column with order date and last updated
 */
export const createDatesColumn = (): ListBuilderColumn => ({
	id: 'dates',
	fields: [
		{
			id: 'date-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'date', objectTable: 'order' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
				formatOptions: { dateFormat: 'datetime-short' },
				allowTextWrap: false,
			},
			displayStyle: 'date',
		},
		{
			id: 'line-break-5',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'updated-date-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: '_modified_date', objectTable: 'order' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
				formatOptions: { dateFormat: 'datetime-short' },
				allowTextWrap: false,
			},
			displayStyle: 'date',
		},
	],
	headers: [
		{ id: 'h4-date', text: 'Date' },
		{ id: 'h4-updated', text: 'Last Updated' },
	],
	minWidth: 110,
	sortable: true,
	alignment: 'left',
	sortFields: [{ direction: 'DESC', fieldName: 'date', objectTable: 'order' }],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

/**
 * Reps column with assigned rep and submitted by
 */
export const createRepsColumn = (): ListBuilderColumn => ({
	id: 'reps',
	fields: [
		{
			id: 'assigned-rep-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'assignedRepName', objectTable: 'order' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
		{
			id: 'line-break-6',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'submitted-by-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'submitterName', objectTable: 'order' },
			},
			styleConfig: {
				// Match the assigned-rep field size ('sm') so both lines of the
				// Reps column render at a consistent font size.
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{ id: 'h5-assigned', text: 'Assigned Rep' },
		{ id: 'h5-submitted', text: 'Submitted By' },
	],
	minWidth: 140,
	sortable: true,
	alignment: 'left',
	sortFields: [
		{ direction: 'ASC', fieldName: 'assignedRepName', objectTable: 'order' },
	],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

/**
 * Order lines column
 */
export const createOrderLinesColumn = (): ListBuilderColumn => ({
	id: 'order-lines',
	fields: [
		{
			id: 'lines-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'numberOfLines', objectTable: 'order' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h6', text: 'Lines' }],
	minWidth: 60,
	sortable: true,
	alignment: 'left',
	sortFields: [
		{ direction: 'DESC', fieldName: 'numberOfLines', objectTable: 'order' },
	],
	widthConfig: { mode: 'flexible', preset: 'small' },
	verticalAlignment: 'top',
});

/**
 * Order costs column with tax, freight, and discount
 */
export const createOrderCostsColumn = (): ListBuilderColumn => ({
	id: 'order-costs',
	fields: [
		{
			id: 'tax-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'tax', objectTable: 'order' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'currency',
		},
		{
			id: 'line-break-7',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'freight-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'freight', objectTable: 'order' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'currency',
		},
		{
			id: 'line-break-8',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'discount-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'discount', objectTable: 'order' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'currency',
		},
	],
	headers: [
		{ id: 'h7-tax', text: 'Tax' },
		{ id: 'h7-freight', text: 'Freight' },
		{ id: 'h7-discount', text: 'Discount' },
	],
	minWidth: 120,
	sortable: true,
	alignment: 'right',
	sortFields: [{ direction: 'DESC', fieldName: 'tax', objectTable: 'order' }],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

/**
 * Order totals column with subtotal and total
 */
export const createOrderTotalsColumn = (): ListBuilderColumn => ({
	id: 'order-totals',
	fields: [
		{
			id: 'subtotal-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'subtotal', objectTable: 'order' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'currency',
		},
		{
			id: 'line-break-9',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'total-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'total', objectTable: 'order' },
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'currency',
		},
	],
	headers: [
		{ id: 'h8-subtotal', text: 'Subtotal' },
		{ id: 'h8-total', text: 'Total' },
	],
	minWidth: 120,
	sortable: true,
	alignment: 'right',
	sortFields: [{ direction: 'DESC', fieldName: 'total', objectTable: 'order' }],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

/**
 * GP totals column
 */
export const createGpTotalsColumn = (): ListBuilderColumn => ({
	id: 'gp-totals',
	fields: [
		{
			id: 'total-gp-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'totalGP', objectTable: 'order' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'currency',
			// Field-level permission keeps the column out of CSV exports.
			// In-table rendering uses GP-name detection in FieldRenderer to
			// show a lock icon, so the header remains visible while cells
			// indicate the lock state explicitly.
			requiredPermission: GP_VIEW_PERMISSION,
		},
	],
	headers: [{ id: 'h-total-gp', text: 'GP' }],
	minWidth: 120,
	sortable: true,
	alignment: 'right',
	sortFields: [
		{ direction: 'DESC', fieldName: 'totalGP', objectTable: 'order' },
	],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

/**
 * Status Text line for the order status column — the ERP status/comment
 * (`order.statusText`, e.g. license-expiration or overcommit notes written
 * back by the integration) rendered as a single truncated line directly
 * below the status badge. Hovering shows the full text (truncation tooltip)
 * and the expand icon opens it in a modal; the line only renders when the
 * order has a status text (`is_not_null`) — no placeholder otherwise.
 *
 * NOT part of createStatusOriginColumn: this stays out of the bundled
 * default lists by design (PD-13616 — only Pharmsource wants it, seeded onto
 * their org list overrides by migration). Exported so the migration can
 * reuse the canonical definition instead of duplicating the JSON. Returns
 * the text field plus the line_break that ends its row — insert both before
 * the origin field.
 */
// noinspection JSUnusedGlobalSymbols - Consumed by the Pharmsource list-override migration in apps/config-api
export const createStatusTextFields = (): ListBuilderColumn['fields'] => [
	{
		id: 'status-text-field',
		dataConfig: {
			type: 'field',
			field: { fieldName: 'statusText', objectTable: 'order' },
		},
		styleConfig: {
			size: 'sm',
			colorRule: { type: 'theme', themeColor: 'textSecondary' },
			textExpansion: { enabled: true, maxLines: 1 },
		},
		displayStyle: 'text',
		conditionalVisibility: {
			field: 'statusText',
			condition: 'is_not_null',
		},
	},
	{
		id: 'status-text-line-break',
		dataConfig: { type: 'custom_text', customText: '' },
		styleConfig: { size: 'md', colorRule: { type: 'default' } },
		displayStyle: 'line_break',
	},
];

/**
 * ERP Comment line for the order status column — the order's single
 * `order_note` of type ERP Comment, exposed on list rows as the derived
 * `erpComment` field by OrdersService. Same treatment and same
 * Pharmsource-only seeding story as createStatusTextFields above.
 */
// noinspection JSUnusedGlobalSymbols - Consumed by the Pharmsource list-override migration in apps/config-api
export const createErpCommentFields = (): ListBuilderColumn['fields'] => [
	{
		id: 'erp-comment-field',
		dataConfig: {
			type: 'field',
			field: { fieldName: 'erpComment', objectTable: 'order' },
		},
		styleConfig: {
			size: 'sm',
			colorRule: { type: 'theme', themeColor: 'textSecondary' },
			textExpansion: { enabled: true, maxLines: 1 },
		},
		displayStyle: 'text',
		conditionalVisibility: {
			field: 'erpComment',
			condition: 'is_not_null',
		},
	},
	{
		id: 'erp-comment-line-break',
		dataConfig: { type: 'custom_text', customText: '' },
		styleConfig: { size: 'md', colorRule: { type: 'default' } },
		displayStyle: 'line_break',
	},
];

/**
 * Payments column (PD-15340) — whether an order has been invoiced and paid,
 * without having to open it.
 *
 * The state word and the money both come from `OrdersService`'s per-order
 * rollup (`paymentState` / `paymentStateLabel` / `amountCollected` /
 * `amountInvoiced`), which reuses the Order Reconciliation report's own
 * billed-and-collected derivation so the list can never contradict the report.
 *
 * Two lines rather than one: the label answers "is this settled?" at a glance,
 * and the collected-of-invoiced pair below it answers "by how much is it off?"
 * — the question that actually gets asked when the label is not "Paid". Both
 * "Not invoiced" and "Over-collected" are called out in colour, since one means
 * revenue not yet billed and the other means money owed back to the customer.
 */
export const createOrderPaymentsColumn = (): ListBuilderColumn => ({
	id: 'order-payments',
	fields: [
		{
			id: 'payment-state-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'paymentStateLabel', objectTable: 'order' },
			},
			styleConfig: {
				size: 'sm',
				fontWeight: 'medium',
				colorRule: {
					type: 'conditional',
					rules: [
						{
							field: { fieldName: 'paymentState' },
							operator: 'equals',
							value: 'paid',
							color: { type: 'success' },
						},
						{
							field: { fieldName: 'paymentState' },
							operator: 'equals',
							value: 'over',
							color: { type: 'danger' },
						},
						{
							field: { fieldName: 'paymentState' },
							operator: 'equals',
							value: 'partial',
							color: { type: 'warning' },
						},
						{
							field: { fieldName: 'paymentState' },
							operator: 'equals',
							value: 'unpaid',
							color: { type: 'warning' },
						},
					],
					defaultColor: { type: 'theme', themeColor: 'textSecondary' },
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'payment-line-break',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'amount-collected-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'amountCollected', objectTable: 'order' },
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'currency',
			// An order with nothing billed and nothing taken reads better as the
			// state word alone than as "$0.00 of $0.00".
			conditionalVisibility: {
				field: 'paymentState',
				condition: 'not_equals',
				value: 'unbilled',
			},
		},
		{
			id: 'amount-of-separator',
			dataConfig: { type: 'custom_text', customText: ' of ' },
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
			conditionalVisibility: {
				field: 'paymentState',
				condition: 'not_equals',
				value: 'unbilled',
			},
		},
		{
			id: 'amount-invoiced-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'amountInvoiced', objectTable: 'order' },
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'currency',
			conditionalVisibility: {
				field: 'paymentState',
				condition: 'not_equals',
				value: 'unbilled',
			},
		},
	],
	headers: [{ id: 'h-payments', text: 'Payments' }],
	// Wide enough that "$10.25 of $10.25" stays on one line under the state
	// word; below this the separator wraps and the cell reads as three lines.
	minWidth: 175,
	sortable: false,
	alignment: 'right',
	widthConfig: { mode: 'flexible', preset: 'large' },
	verticalAlignment: 'top',
});

/**
 * Standard column set for all order lists
 */
export const createStandardOrderColumns = (): Array<ListBuilderColumn> => [
	createStatusOriginColumn(),
	createOrderErpNumbersColumn(),
	createClientInfoColumn(),
	createDatesColumn(),
	createRepsColumn(),
	createOrderLinesColumn(),
	createOrderCostsColumn(),
	createOrderTotalsColumn(),
	createOrderPaymentsColumn(),
	createGpTotalsColumn(),
];

/**
 * Routing column for the bundled 'Routing' list (PD-15465): whether the
 * order is on a trip. Renders "On Trip #N" linking to the trip when routed
 * (order.fk_trip_stop__id → trip_stop → trip, flattened onto list rows as
 * `tripId` by OrdersService), or a muted "Not routed" otherwise.
 */
export const createRoutingColumn = (): ListBuilderColumn => ({
	id: 'routing',
	fields: [
		{
			id: 'trip-link-field',
			dataConfig: {
				type: 'template',
				template: 'On Trip #{tripId}',
				templateFields: [{ fieldName: 'tripId', objectTable: 'order' }],
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'link' },
			},
			displayStyle: 'text',
			link: {
				type: 'custom',
				enabled: true,
				urlTemplate: '/distribution/trip/{tripId}',
			},
			conditionalVisibility: { field: 'tripId', condition: 'is_not_null' },
		},
		{
			id: 'not-routed-field',
			dataConfig: { type: 'custom_text', customText: 'Not routed' },
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
			conditionalVisibility: { field: 'tripId', condition: 'is_null' },
		},
	],
	headers: [{ id: 'h-routing', text: 'Routed' }],
	sortable: false,
});

/**
 * Client route-blueprint membership for the bundled 'Routing' list
 * (PD-15465). An unrouted order for a client who is a stop on an active
 * route gets picked up by that route's next run, while a route-less client
 * needs manual dispatch — so the list shows which routes (if any) will
 * cover the client. OrdersService flattens the memberships onto rows as
 * `clientRoutes` ([{ id, name }]); the display caps at two names with a
 * "+N more" tail listing the rest in a tooltip.
 */
export const createClientRoutesColumn = (): ListBuilderColumn => ({
	id: 'client-routes',
	fields: [
		{
			id: 'client-routes-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'clientRoutes', objectTable: 'order' },
				// Empty string (not the default '-') so the display component's
				// emptyText handles the no-routes state.
				defaultValue: '',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'related_entity_list',
			displayConfig: {
				entityType: 'route',
				icon: 'route',
				linkPattern: '/distribution/route/{id}',
				nameField: 'name',
				maxDisplay: 2,
				emptyText: 'No routes',
			},
		},
	],
	headers: [{ id: 'h-client-routes', text: 'Client Routes' }],
	sortable: false,
});
